/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * PRIJZEN EXCL. BTW: GEDEELDE REKENREGEL (issue #8)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Zelfde regel als snippets/ab-price-excl.liquid:
 * - exact bedrag excl. btw: incl. btw teruggerekend met het tarief van het land, afgerond op de dichtstbijzijnde
 *   cent (= de prijs excl. btw zoals die in de admin staat);
 * - weergave: naar boven afgerond op hele euro's, zodat de getoonde prijs nooit lager is dan de factuur.
 * Het tarief komt uit window.themeVariables.settings.vatRate (snippets/js-variables.liquid).
 */
window.abPrice = (function() {
  'use strict';

  function vatDivisor() {
    const rate = Number(window.themeVariables?.settings?.vatRate);
    return 100 + (Number.isFinite(rate) ? rate : 21);
  }

  // Incl. btw in centen -> exacte prijs excl. btw in centen
  function exclCents(inclCents) {
    const divisor = vatDivisor();
    return Math.floor((Math.round(inclCents) * 100 + Math.floor(divisor / 2)) / divisor);
  }

  // Exacte prijs excl. btw in centen -> weergave in hele euro's, naar boven afgerond
  function displayCents(exclCentsValue) {
    return Math.ceil(exclCentsValue / 100) * 100;
  }

  function formatMoney(cents) {
    return typeof window.formatMoney === 'function'
      ? window.formatMoney(cents)
      : (cents / 100).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Zelfde notatie als de Liquid-filter money_without_trailing_zeros
  function formatWithoutTrailingZeros(cents) {
    const formatted = formatMoney(cents);
    return cents % 100 === 0 ? formatted.replace(/[.,]00(?!\d)/, '') : formatted;
  }

  // Getoonde prijsnotatie ("1.257,-", "€ 1.257,19", "€1,257.19") -> centen
  function parseCents(text) {
    let cleaned = String(text || '').replace(/[^0-9,.]/g, '');
    if (!cleaned) return 0;
    const lastSeparator = Math.max(cleaned.lastIndexOf(','), cleaned.lastIndexOf('.'));
    const decimals = lastSeparator === -1 ? '' : cleaned.slice(lastSeparator + 1);
    if (lastSeparator !== -1 && decimals.length > 0 && decimals.length <= 2) {
      cleaned = cleaned.slice(0, lastSeparator).replace(/[.,]/g, '') + '.' + decimals;
    } else {
      cleaned = cleaned.replace(/[.,]/g, '');
    }
    const value = parseFloat(cleaned);
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
  }

  return { exclCents, displayCents, formatMoney, formatWithoutTrailingZeros, parseCents };
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * GA4 ECOMMERCE EVENT TRACKING
 * ------------------------------------------------------------------------------------------------------------
 * 
 * Dit script luistert naar Focal theme events en pusht GA4 ecommerce events naar de dataLayer.
 * 
 * Events:
 * - add_to_cart: Wanneer een product wordt toegevoegd via variant:added
 * - remove_from_cart: Wanneer een product wordt verwijderd uit de cart
 * - begin_checkout: Wanneer de checkout knop wordt geklikt
 * - select_item: Wanneer een product wordt aangeklikt in een lijst
 * 
 * Prijzen worden berekend excl. btw met het tarief van het land (window.abPrice).
 * 
 * Gemigreerd vanuit Lightspeed webshop - januari 2026
 */
(function() {
  'use strict';
  
  const CURRENCY = 'EUR';
  
  /**
   * Bereken prijs excl. btw
   * @param {number} priceInCents - Prijs in centen (incl. btw)
   * @returns {number} Exacte prijs excl. btw in euro's (2 decimalen)
   */
  function calculateExclVat(priceInCents) {
    return window.abPrice.exclCents(priceInCents) / 100;
  }
  
  /**
   * Track add_to_cart event
   */
  document.addEventListener('variant:added', function(event) {
    const variant = event.detail.variant;
    const quantity = event.detail.quantity || 1;
    
    if (!variant) return;
    
    const priceExcl = calculateExclVat(variant.price);
    
    window.dataLayer = window.dataLayer || [];
    
    // Clear ecommerce object
    dataLayer.push({ ecommerce: null });
    
    // Push add_to_cart event
    dataLayer.push({
      'event': 'add_to_cart',
      'ecommerce': {
        'currency': CURRENCY,
        'value': priceExcl * quantity,
        'items': [{
          'item_id': String(variant.product_id),
          'item_name': variant.product_title || variant.name,
          'item_brand': variant.vendor || '',
          'item_category': variant.product_type || '',
          'item_variant': String(variant.id),
          'price': priceExcl,
          'quantity': quantity
        }]
      }
    });
    
    console.log('[GA4] add_to_cart:', variant.product_title, 'qty:', quantity);
  });
  
  /**
   * Track cart changes voor remove_from_cart
   * We vergelijken de oude cart met de nieuwe om te bepalen wat verwijderd is
   */
  let previousCart = null;
  
  document.addEventListener('cart:updated', function(event) {
    const cart = event.detail.cart;
    
    if (!cart || !previousCart) {
      previousCart = cart;
      return;
    }
    
    // Vergelijk items om te zien wat verwijderd of verminderd is
    const previousItems = {};
    previousCart.items.forEach(item => {
      previousItems[item.key] = item;
    });
    
    const currentItems = {};
    cart.items.forEach(item => {
      currentItems[item.key] = item;
    });
    
    // Check voor verwijderde of verminderde items
    Object.keys(previousItems).forEach(key => {
      const prevItem = previousItems[key];
      const currItem = currentItems[key];
      
      let removedQty = 0;
      
      if (!currItem) {
        // Item volledig verwijderd
        removedQty = prevItem.quantity;
      } else if (currItem.quantity < prevItem.quantity) {
        // Quantity verminderd
        removedQty = prevItem.quantity - currItem.quantity;
      }
      
      if (removedQty > 0) {
        const priceExcl = calculateExclVat(prevItem.price);
        
        window.dataLayer = window.dataLayer || [];
        dataLayer.push({ ecommerce: null });
        
        dataLayer.push({
          'event': 'remove_from_cart',
          'ecommerce': {
            'currency': CURRENCY,
            'value': priceExcl * removedQty,
            'items': [{
              'item_id': String(prevItem.product_id),
              'item_name': prevItem.product_title,
              'item_variant': String(prevItem.variant_id),
              'item_brand': prevItem.vendor,
              'price': priceExcl,
              'quantity': removedQty
            }]
          }
        });
        
        console.log('[GA4] remove_from_cart:', prevItem.product_title, 'qty:', removedQty);
      }
    });
    
    previousCart = cart;
  });
  
  /**
   * Track begin_checkout event
   * Luistert naar checkout knoppen
   */
  function trackBeginCheckout() {
    // Checkout knoppen in mini-cart en cart pagina
    const checkoutButtons = document.querySelectorAll('[name="checkout"], [href*="/checkout"], .shopify-payment-button button, form[action*="/checkout"] button[type="submit"]');
    
    checkoutButtons.forEach(button => {
      if (button.hasAttribute('data-ga4-checkout-tracked')) return;
      button.setAttribute('data-ga4-checkout-tracked', 'true');
      
      button.addEventListener('click', function() {
        // Haal cart data op via Shopify AJAX API
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            if (!cart.items || cart.items.length === 0) return;
            
            const cartTotalExcl = calculateExclVat(cart.total_price);
            
            const items = cart.items.map((item, index) => ({
              'item_id': String(item.product_id),
              'item_name': item.product_title,
              'item_variant': String(item.variant_id),
              'item_brand': item.vendor,
              'price': calculateExclVat(item.price),
              'quantity': item.quantity,
              'index': index
            }));
            
            window.dataLayer = window.dataLayer || [];
            dataLayer.push({ ecommerce: null });
            
            dataLayer.push({
              'event': 'begin_checkout',
              'ecommerce': {
                'currency': CURRENCY,
                'value': cartTotalExcl,
                'items': items
              }
            });
            
            console.log('[GA4] begin_checkout, value:', cartTotalExcl);
          })
          .catch(err => console.error('[GA4] Error fetching cart:', err));
      });
    });
  }
  
  /**
   * Track select_item event
   * Wanneer een product wordt aangeklikt in een lijst/grid
   */
  function trackSelectItem() {
    // Product links in collecties en zoekresultaten
    const productLinks = document.querySelectorAll('.product-item a[href*="/products/"], .product-list a[href*="/products/"]');
    
    productLinks.forEach(link => {
      if (link.hasAttribute('data-ga4-select-tracked')) return;
      link.setAttribute('data-ga4-select-tracked', 'true');
      
      link.addEventListener('click', function(e) {
        // Zoek product data in parent element
        const productItem = link.closest('.product-item, [data-product-id]');
        if (!productItem) return;
        
        // Probeer product ID te vinden
        const productId = productItem.dataset.productId || 
                         link.href.match(/\/products\/([^/?]+)/)?.[1] || 
                         '';
        
        // Probeer product naam te vinden
        const titleElement = productItem.querySelector('.product-item__title, .product-card__title, h3, h4');
        const productName = titleElement ? titleElement.textContent.trim() : '';
        
        // Probeer prijs te vinden
        const priceElement = productItem.querySelector('.price--incl-vat, .price, .product-item__price');
        const priceCents = priceElement ? window.abPrice.parseCents(priceElement.textContent) : 0;
        
        // Probeer list name te bepalen
        const listContainer = link.closest('[data-collection-id], .collection, .search-results');
        const listName = listContainer?.dataset?.collectionTitle || 
                        document.querySelector('h1')?.textContent.trim() || 
                        'Product List';
        
        window.dataLayer = window.dataLayer || [];
        dataLayer.push({ ecommerce: null });
        
        dataLayer.push({
          'event': 'select_item',
          'ecommerce': {
            'currency': CURRENCY,
            'item_list_name': listName,
            'items': [{
              'item_id': productId,
              'item_name': productName,
              'price': priceCents > 0 ? calculateExclVat(priceCents) : 0,
              'item_list_name': listName
            }]
          }
        });
        
        console.log('[GA4] select_item:', productName);
      });
    });
  }
  
  /**
   * Initialiseer tracking
   */
  function initGA4Tracking() {
    trackBeginCheckout();
    trackSelectItem();
  }
  
  // Start tracking wanneer DOM geladen is
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGA4Tracking);
  } else {
    initGA4Tracking();
  }
  
  // Herinitialiseer na cart updates (nieuwe checkout knoppen kunnen verschijnen)
  document.addEventListener('cart:updated', function() {
    setTimeout(initGA4Tracking, 500);
  });
  
  // Herinitialiseer na dynamische content loads
  const observer = new MutationObserver(function(mutations) {
    let shouldReinit = false;
    mutations.forEach(mutation => {
      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1 && (
            node.querySelector?.('[name="checkout"]') ||
            node.querySelector?.('.product-item') ||
            node.matches?.('[name="checkout"]') ||
            node.matches?.('.product-item')
          )) {
            shouldReinit = true;
          }
        });
      }
    });
    if (shouldReinit) {
      setTimeout(initGA4Tracking, 100);
    }
  });
  
  observer.observe(document.body, { childList: true, subtree: true });
  
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * PRIJS EXCL. BTW BIJWERKEN OP DE PRODUCTPAGINA (v3, issue #8)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Liquid rendert het prijspaar al correct (snippets/ab-price-excl.liquid). Dit script werkt het alleen bij
 * wanneer Globo Product Options een optie met meerprijs toevoegt.
 *
 * - Basisprijs: data-incl-cents op [data-price-wrapper] (exacte prijs incl. btw van de variant).
 * - Meerprijs: elke gekozen Globo-optie heeft gpo-data-variant-id en gpo-data-product-handle van een
 *   hulpproduct; de exacte prijs daarvan komt uit /products/<handle>.js (per land, zoals de checkout).
 * - Er wordt niet meer gerekend met de getoonde (afgeronde) prijstekst.
 * - De sticky koopbalk krijgt ook de incl.-prijs; in het hoofdblok schrijft Globo die zelf.
 */
(function() {
  'use strict';

  const helperPrices = new Map(); // handle -> Promise<Map<variantId, priceCents>>
  let updateToken = 0;

  function fetchHelperPrices(handle) {
    if (!helperPrices.has(handle)) {
      const url = `${window.themeVariables?.routes?.rootUrlWithoutSlash || ''}/products/${encodeURIComponent(handle)}.js`;
      const request = fetch(url, { credentials: 'same-origin' })
        .then(response => (response.ok ? response.json() : null))
        .then(product => new Map((product?.variants || []).map(variant => [String(variant.id), variant.price])))
        .catch(() => {
          helperPrices.delete(handle); // later opnieuw proberen
          return new Map();
        });
      helperPrices.set(handle, request);
    }
    return helperPrices.get(handle);
  }

  function selectedGloboOptions(scope) {
    return [...scope.querySelectorAll('[gpo-data-variant-id][gpo-data-product-handle]')].filter(element => {
      if (element.tagName === 'OPTION') return element.selected && !element.disabled;
      if (element.tagName === 'INPUT') return element.checked && !element.disabled;
      return false;
    });
  }

  async function surchargeCents(scope) {
    const options = selectedGloboOptions(scope);
    if (!options.length) return 0;

    const prices = await Promise.all(options.map(async element => {
      const variantPrices = await fetchHelperPrices(element.getAttribute('gpo-data-product-handle'));
      return variantPrices.get(element.getAttribute('gpo-data-variant-id')) || 0;
    }));
    return prices.reduce((total, price) => total + price, 0);
  }

  function setText(element, text) {
    if (element && element.textContent.trim() !== text) {
      element.textContent = text;
    }
  }

  async function updateWrappers() {
    const token = ++updateToken;
    const wrappers = [...document.querySelectorAll('[data-price-wrapper][data-incl-cents]')];
    if (!wrappers.length) return;

    // Globo-opties staan in dezelfde sectie als het prijsblok; de sticky koopbalk hoort bij die sectie
    const scopes = new Map();
    for (const wrapper of wrappers) {
      const scope = wrapper.closest('.shopify-section') || document;
      if (!scopes.has(scope)) scopes.set(scope, surchargeCents(scope));
    }

    for (const wrapper of wrappers) {
      const scope = wrapper.closest('.shopify-section') || document;
      const surcharge = await scopes.get(scope);
      if (token !== updateToken) return; // er loopt al een nieuwere update

      const inclCents = (parseInt(wrapper.dataset.inclCents, 10) || 0) + surcharge;
      if (!inclCents) continue;

      const exclDisplay = window.abPrice.displayCents(window.abPrice.exclCents(inclCents));
      setText(wrapper.querySelector('[data-price-excl-amount]'), window.abPrice.formatWithoutTrailingZeros(exclDisplay));

      if (wrapper.classList.contains('product-sticky-form__price-wrapper')) {
        setText(wrapper.querySelector('[data-price-incl] .price'), window.abPrice.formatMoney(inclCents));
      }
    }
  }

  let scheduled = false;
  function scheduleUpdate() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      updateWrappers();
    });
  }

  // Globo-keuzes (selects, radio's, checkboxes)
  document.addEventListener('change', event => {
    if (event.target.closest?.('[class*="gpo"]') || event.target.hasAttribute?.('gpo-data-variant-id')) {
      scheduleUpdate();
    }
  });

  // Globo past de incl.-prijs in het hoofdblok aan zodra zijn opties geladen of gewijzigd zijn
  const priceObserver = new MutationObserver(scheduleUpdate);
  function observeInclPrices() {
    priceObserver.disconnect();
    document.querySelectorAll('[data-price-wrapper] [data-price-incl]').forEach(element => {
      priceObserver.observe(element, { childList: true, subtree: true, characterData: true });
    });
  }

  // Variantwissel: de sticky koopbalk wordt niet opnieuw gerenderd, dus basisprijs zelf bijwerken
  // (het hoofdblok ook, voor het geval er geen section-rerender volgt)
  document.addEventListener('variant:changed', event => {
    const variant = event.detail?.variant;
    if (variant) {
      document.querySelectorAll('[data-price-wrapper][data-incl-cents]').forEach(wrapper => {
        wrapper.dataset.inclCents = String(variant.price);
      });
    }
    scheduleUpdate();
  });

  // Dubbele rAF: wacht tot Focal de nieuwe markup heeft ingevoegd
  document.addEventListener('product:rerender', () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        observeInclPrices();
        scheduleUpdate();
      });
    });
  });

  function init() {
    observeInclPrices();
    scheduleUpdate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
